import { makeAutoObservable, runInAction } from 'mobx';
import { llmConfigApi, type RawLLMDocument } from '@/services/llmConfigApi';
import type {
  AccessAppPolicy,
  AccessPolicyDocument,
  AppConfigVersionMeta,
  GatewayCatalogValidation,
  LLMConfigValidation,
} from '@/types';
import { setJsonPointerValue } from '@/utils/llmConfigDocument';

function errorMessage(error: unknown, fallback: string): string {
  const status = (
    error as { response?: { status?: number; data?: { error?: string } } }
  )?.response?.status;
  if (status === 409) {
    return '配置已被其他管理员更新，请刷新页面后重新确认 Diff';
  }
  return error instanceof Error ? error.message : fallback;
}

function hasPendingDeployment(deployments: AppConfigVersionMeta[]): boolean {
  return deployments.some((deployment) =>
    ['BAKING', 'DEPLOYING', 'VALIDATING'].includes(
      (deployment.deployment_state || '').toUpperCase(),
    ),
  );
}

class LLMRouteConfigStore {
  document: RawLLMDocument | null = null;
  originalDocument: RawLLMDocument | null = null;
  gateway: GatewayCatalogValidation | null = null;
  baseVersion = 0;
  latestHostedVersion = 0;
  currentVersion: AppConfigVersionMeta | null = null;
  recentHostedVersions: AppConfigVersionMeta[] = [];
  recentDeployments: AppConfigVersionMeta[] = [];
  loading = false;
  validating = false;
  publishing = false;
  error = '';
  warning = '';
  validation: LLMConfigValidation | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get deploymentPending(): boolean {
    return hasPendingDeployment(this.recentDeployments);
  }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const view = await llmConfigApi.getGatewayCatalog();
      runInAction(() => {
        this.document = structuredClone(view.document);
        this.originalDocument = structuredClone(view.document);
        this.gateway = view.gateway || null;
        this.baseVersion = view.baseVersion;
        this.latestHostedVersion = view.latestHostedVersion;
        this.currentVersion = view.currentVersion;
        this.recentHostedVersions = view.recentHostedVersions;
        this.recentDeployments = view.recentDeployments;
        this.warning = view.validationWarning || '';
        this.validation = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '加载 LLM 路由失败');
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  updateRoute(
    publicModelID: string,
    routeID: string,
    pointer: string,
    field: 'priority' | 'weight' | 'enabled' | 'provider' | 'provider_model_id',
    value: number | boolean | string,
  ) {
    if (!this.document) return;
    this.document = setJsonPointerValue(this.document, pointer, field, value);
    if (this.gateway) {
      this.gateway = {
        ...this.gateway,
        models: this.gateway.models.map((model) =>
          model.public_model_id !== publicModelID
            ? model
            : {
                ...model,
                routes: model.routes.map((route) =>
                  route.route_id === routeID
                    ? { ...route, [field]: value }
                    : route,
                ),
              },
        ),
      };
    }
    this.validation = null;
  }

  async validate(): Promise<boolean> {
    if (!this.document) return false;
    this.validating = true;
    this.error = '';
    try {
      const validation = await llmConfigApi.validateGatewayCatalog(
        this.document,
      );
      runInAction(() => {
        this.validation = validation;
        this.gateway = validation.gateway;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '路由配置校验失败');
      });
      return false;
    } finally {
      runInAction(() => {
        this.validating = false;
      });
    }
  }

  async publish(): Promise<boolean> {
    if (!this.document || this.baseVersion <= 0) return false;
    this.publishing = true;
    this.error = '';
    try {
      await llmConfigApi.publishGatewayCatalog({
        document: this.document,
        baseVersion: this.baseVersion,
      });
      await this.load();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '路由配置发布失败');
      });
      return false;
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }

  async rollback(targetVersion: number): Promise<boolean> {
    const baseVersion = this.latestHostedVersion || this.baseVersion;
    if (baseVersion <= 0) return false;
    this.publishing = true;
    this.error = '';
    try {
      await llmConfigApi.rollbackGatewayCatalog({
        targetVersion,
        baseVersion,
      });
      await this.load();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '路由配置回滚失败');
      });
      return false;
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }
}

class LLMAccessConfigStore {
  document: AccessPolicyDocument | null = null;
  originalDocument: AccessPolicyDocument | null = null;
  baseVersion = 0;
  latestHostedVersion = 0;
  currentVersion: AppConfigVersionMeta | null = null;
  recentHostedVersions: AppConfigVersionMeta[] = [];
  recentDeployments: AppConfigVersionMeta[] = [];
  loading = false;
  validating = false;
  publishing = false;
  error = '';
  warning = '';
  validation: LLMConfigValidation | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  get deploymentPending(): boolean {
    return hasPendingDeployment(this.recentDeployments);
  }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      const view = await llmConfigApi.getAccessPolicy();
      runInAction(() => {
        this.document = structuredClone(view.document);
        this.originalDocument = structuredClone(view.document);
        this.baseVersion = view.baseVersion;
        this.latestHostedVersion = view.latestHostedVersion;
        this.currentVersion = view.currentVersion;
        this.recentHostedVersions = view.recentHostedVersions;
        this.recentDeployments = view.recentDeployments;
        this.warning = view.validationWarning || '';
        this.validation = null;
      });
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '加载 LLM 访问策略失败');
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  updateApp(appID: string, policy: AccessAppPolicy, previousAppID?: string) {
    if (!this.document) return;
    const apps = { ...this.document.apps };
    if (previousAppID && previousAppID !== appID) {
      delete apps[previousAppID];
    }
    apps[appID] = policy;
    this.document = { ...this.document, apps };
    this.validation = null;
  }

  async validate(): Promise<boolean> {
    if (!this.document) return false;
    this.validating = true;
    this.error = '';
    try {
      const validation = await llmConfigApi.validateAccessPolicy(this.document);
      runInAction(() => {
        this.validation = validation;
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '访问策略校验失败');
      });
      return false;
    } finally {
      runInAction(() => {
        this.validating = false;
      });
    }
  }

  async publish(): Promise<boolean> {
    if (!this.document || this.baseVersion <= 0) return false;
    this.publishing = true;
    this.error = '';
    try {
      await llmConfigApi.publishAccessPolicy({
        document: this.document,
        baseVersion: this.baseVersion,
      });
      await this.load();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '访问策略发布失败');
      });
      return false;
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }

  async rollback(targetVersion: number): Promise<boolean> {
    const baseVersion = this.latestHostedVersion || this.baseVersion;
    if (baseVersion <= 0) return false;
    this.publishing = true;
    this.error = '';
    try {
      await llmConfigApi.rollbackAccessPolicy({
        targetVersion,
        baseVersion,
      });
      await this.load();
      return true;
    } catch (error) {
      runInAction(() => {
        this.error = errorMessage(error, '访问策略回滚失败');
      });
      return false;
    } finally {
      runInAction(() => {
        this.publishing = false;
      });
    }
  }
}

export const llmRouteConfigStore = new LLMRouteConfigStore();
export const llmAccessConfigStore = new LLMAccessConfigStore();
